import { createElement, useEffect } from 'react'
import { toast } from 'sonner'

import { PasskeyIcon } from '@/components/shared/auth-icons'
import { registerPasskeyOnThisDevice } from '@/lib/passkey'
import { isPasskeyAvailableHere } from '@/lib/supabase'
import { useAuthPreferenceStore } from '@/store/useAuthPreferenceStore'

/** 起動直後の他の表示（前回ページへの復帰確認など）と重ならないよう少し待つ */
const PROMPT_DELAY_MS = 1_500

const handleRegister = async () => {
  const result = await registerPasskeyOnThisDevice()
  if (result === 'registered') toast.success('パスキーを登録しました。次回からパスキーでサインインできます')
  if (result === 'already-registered') toast.success('この端末のパスキーは登録済みです')
  if (result === 'failed')
    toast.error('パスキーを登録できませんでした。アカウントメニューの「パスキー」から登録できます')
}

/**
 * Google でサインインした端末に、1 回だけパスキーの登録を案内する
 *
 * 確認ダイアログは他の確認（前回ページへの復帰など）を閉じてしまうため、トーストのボタンで案内する。
 * パスキーの作成はユーザー操作から呼ぶ必要がある（iOS Safari）ので、自動では始めない。
 */
export const usePasskeyNudge = (enabled: boolean): void => {
  const lastSignInMethod = useAuthPreferenceStore((state) => state.lastSignInMethod)
  const passkeyPromptShown = useAuthPreferenceStore((state) => state.passkeyPromptShown)
  const markPasskeyPromptShown = useAuthPreferenceStore((state) => state.markPasskeyPromptShown)

  useEffect(() => {
    if (!enabled || passkeyPromptShown || lastSignInMethod !== 'google') return
    if (!isPasskeyAvailableHere()) return

    const timer = setTimeout(() => {
      markPasskeyPromptShown()
      toast('この端末でパスキーを登録しますか？', {
        description: 'パスワードもメールも不要で、次回から Face ID・Touch ID などで素早くサインインできます',
        icon: createElement(PasskeyIcon, { className: 'size-5' }),
        duration: 20_000,
        action: { label: '登録する', onClick: () => void handleRegister() },
        cancel: { label: 'あとで', onClick: () => undefined },
      })
    }, PROMPT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [enabled, passkeyPromptShown, lastSignInMethod, markPasskeyPromptShown])
}
